// ChangePasswordPage: eigen wachtwoord wijzigen. Verplicht na een eerste
// login met een tijdelijk wachtwoord, daarna vrijwillig via de kopbalk.

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from '@rvdh/react-auth-context';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../api/client.js';

const MIN_LENGTH = 12;

function translateError(err) {
    const msg = err?.message || '';
    if (err?.statusCode === 401 && /current/i.test(msg)) { return 'Het huidige wachtwoord klopt niet.'; }
    if (/at least/i.test(msg)) { return `Het nieuwe wachtwoord moet minimaal ${MIN_LENGTH} tekens hebben.`; }
    if (/differ/i.test(msg)) { return 'Het nieuwe wachtwoord moet anders zijn dan het huidige.'; }
    if (!err?.statusCode) { return 'Geen verbinding met de server. Probeer het opnieuw.'; }
    return 'Wachtwoord wijzigen is mislukt. Probeer het opnieuw.';
}

export default function ChangePasswordPage() {
    const { passwordChangeRequired, setPasswordChangeRequired, logout } = useAuth();
    const navigate = useNavigate();
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [repeat, setRepeat] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const tooShort = next.length > 0 && next.length < MIN_LENGTH;
    const mismatch = repeat.length > 0 && repeat !== next;
    const canSubmit = current && next.length >= MIN_LENGTH && repeat === next && !loading;

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!canSubmit) { return; }
        setLoading(true);
        setError(null);
        try {
            await api.post(endpoints.changePassword, { current_password: current, new_password: next });
            setPasswordChangeRequired(false);
            navigate('/', { replace: true });
        } catch (err) {
            setError(translateError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
            <Container maxWidth="xs">
                <Card variant="outlined">
                    <CardContent sx={{ p: 4 }}>
                        <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>Wachtwoord wijzigen</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {passwordChangeRequired
                                ? 'Je bent ingelogd met een tijdelijk wachtwoord. Kies een eigen wachtwoord om verder te gaan.'
                                : `Kies een nieuw wachtwoord van minimaal ${MIN_LENGTH} tekens.`}
                        </Typography>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <form onSubmit={handleSubmit} noValidate>
                            <Stack spacing={2}>
                                <TextField
                                    label="Huidig wachtwoord"
                                    type="password"
                                    autoComplete="current-password"
                                    value={current}
                                    onChange={(e) => setCurrent(e.target.value)}
                                    required
                                    autoFocus
                                />
                                <TextField
                                    label="Nieuw wachtwoord"
                                    type="password"
                                    autoComplete="new-password"
                                    value={next}
                                    onChange={(e) => setNext(e.target.value)}
                                    error={tooShort}
                                    helperText={`Minimaal ${MIN_LENGTH} tekens`}
                                    required
                                />
                                <TextField
                                    label="Herhaal nieuw wachtwoord"
                                    type="password"
                                    autoComplete="new-password"
                                    value={repeat}
                                    onChange={(e) => setRepeat(e.target.value)}
                                    error={mismatch}
                                    helperText={mismatch ? 'De wachtwoorden zijn niet gelijk' : undefined}
                                    required
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={!canSubmit}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                                >
                                    Wachtwoord opslaan
                                </Button>
                                {passwordChangeRequired ? (
                                    <Button onClick={() => { logout(); navigate('/login', { replace: true }); }}>Uitloggen</Button>
                                ) : (
                                    <Button onClick={() => navigate(-1)}>Annuleren</Button>
                                )}
                            </Stack>
                        </form>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}
